/**
 * 钱包侧「切链 / 加链」统一入口（v2.1.0 多链）
 * ---------------------------------------------------------------------------
 * 历史问题：老实现在这里把支持链写死在代码里，且只在 BinanceChain 上切，
 * 换一条链就得改代码。现在所有参数都取自 config/chains.js 注册表。
 *
 * 约定：
 *   - 没有钱包（未安装 / 未注入）时**不算失败**：应用内仍可切换数据源链，
 *     只是不发钱包请求（返回 { ok: true, skipped: true }）。
 *   - 用户主动拒绝（4001）如实返回，调用方据此回滚选择。
 */

import { CHAIN_REGISTRY, toHexChainId } from '@/config/chains';

/** 取注入式钱包 provider（MetaMask / 兼容钱包；优先 providers[0]） */
export function getInjectedProvider() {
  if (typeof window === 'undefined') {
    return null;
  }
  if (window.ethereum && Array.isArray(window.ethereum.providers) && window.ethereum.providers.length) {
    return window.ethereum.providers[0];
  }
  return window.ethereum || null;
}

/**
 * 把钱包切到某条注册表里的链；钱包里没有这条链时自动 wallet_addEthereumChain。
 * @param {string} key CHAIN_REGISTRY 的 key（如 'bsc' / 'bscTest'）
 * @returns {Promise<{ok: boolean, skipped?: boolean, added?: boolean, reason?: string}>}
 */
export async function ensureWalletChain(key) {
  const chain = CHAIN_REGISTRY[key];
  if (!chain) {
    return { ok: false, reason: 'unknown-chain' };
  }
  const provider = getInjectedProvider();
  if (!provider || typeof provider.request !== 'function') {
    return { ok: true, skipped: true };
  }

  const hex = toHexChainId(chain.chainId);
  try {
    await provider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: hex }],
    });
    return { ok: true };
  } catch (err) {
    const code = err && err.code;
    // 4902 = 钱包里还没有这条链；部分钱包用 -32603 表示未知链
    if (code === 4902 || code === -32603) {
      try {
        // blockExplorerUrls 为空数组时部分钱包（MetaMask 老版本）会直接报错，
        // 新链接入初期往往还没有浏览器地址（如 BOT），空值就不要传这个字段。
        const params = {
          chainId: hex,
          chainName: chain.chainName,
          nativeCurrency: chain.nativeCurrency,
          rpcUrls: chain.rpcUrls,
        };
        if (Array.isArray(chain.blockExplorerUrls) && chain.blockExplorerUrls.length) {
          params.blockExplorerUrls = chain.blockExplorerUrls;
        }
        await provider.request({
          method: 'wallet_addEthereumChain',
          params: [params],
        });
        return { ok: true, added: true };
      } catch (addErr) {
        return { ok: false, reason: (addErr && (addErr.code || addErr.message)) || 'add-failed' };
      }
    }
    // 4001 = 用户拒绝
    return { ok: false, reason: code || (err && err.message) || 'switch-failed' };
  }
}

/** 当前钱包所在链是否在注册表里（后台切链提示用） */
export function isSupportedChain(chainIdHex) {
  const id = Number(chainIdHex);
  return Object.values(CHAIN_REGISTRY).some((c) => Number(c.chainId) === id);
}
